import React, { useEffect, useState } from 'react';
import { Box, Button, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';
import { getAutoAttendanceSettings, saveAutoAttendanceSettings } from '../../api/autoAttendance';
import { errorMessages } from '../../utils/methods';

const MODE_OPTIONS = [
  { value: 'ABSENT', label: '자동 결석' },
  { value: 'ATTEND', label: '자동 출석' },
  { value: 'OFF', label: '끄기 (미확인으로 둠)' },
];

function AutoAttendanceSettings({ branchId }) {
  const user = useSelector(state => state.user.user);
  const toast = useToast();
  const queryClient = useQueryClient();
  const [modes, setModes] = useState({});

  const { data, isLoading, isError } = useQuery({
    queryKey: ['autoAttendanceSettings', branchId],
    queryFn: () => getAutoAttendanceSettings(branchId, user.token),
    enabled: !!branchId,
  });

  useEffect(() => {
    if (data?.items) {
      setModes(Object.fromEntries(data.items.map(item => [item.classType, item.mode])));
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () =>
      saveAutoAttendanceSettings(
        branchId,
        { items: (data?.items || []).map(item => ({ classType: item.classType, mode: modes[item.classType] })) },
        user.token,
      ),
    onSuccess: () => {
      toast.success('설정이 저장되었습니다');
      queryClient.invalidateQueries({ queryKey: ['autoAttendanceSettings'] });
    },
    onError: error => toast.error(errorMessages(error)),
  });

  return (
    <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <Box
        sx={{
          background: '#fff', borderRadius: '12px', border: '1px solid #E8E8E8',
          padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px',
        }}>
        <Box>
          <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#262626' }}>자동 출결</Typography>
          <Typography sx={{ fontSize: '13px', color: '#888', mt: '4px' }}>
            수업 종료 후 출결을 처리하지 않은 예약을 6시간마다(0·6·12·18시) 자동 처리합니다. 끄면 수업 관리
            메뉴에서 직접 처리합니다.
          </Typography>
          <Typography sx={{ fontSize: '13px', color: '#d32f2f', mt: '4px' }}>
            끄기에서 자동 결석·자동 출석으로 바꾸면, 꺼 둔 동안 쌓인 출결 미확인 예약도 다음 실행 때 모두 한꺼번에
            처리됩니다(횟수 차감 포함).
          </Typography>
        </Box>

        {isLoading && <Typography sx={{ fontSize: '14px', color: '#888' }}>불러오는 중...</Typography>}
        {isError && (
          <Typography sx={{ fontSize: '14px', color: '#d32f2f' }}>설정을 불러오지 못했습니다.</Typography>
        )}

        {/* 온라인 레슨 자동 출결 숨김 (2026-10-16). 저장 시 기존 값은 그대로 다시 보낸다.
        {(data?.items || []).map(item => ( */}
        {(data?.items || []).filter(item => item.classType !== 'ONLINE_LESSON').map(item => (
          <Box
            key={item.classType}
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px',
              padding: '16px 20px', background: '#F7F9FA', borderRadius: '8px',
            }}>
            <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#333' }}>{item.classTypeLabel}</Typography>
            <RadioGroup
              row
              aria-label={`${item.classTypeLabel} 자동 출결`}
              value={modes[item.classType] || 'ABSENT'}
              onChange={event => setModes(prev => ({ ...prev, [item.classType]: event.target.value }))}>
              {MODE_OPTIONS.map(option => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#262626' } }} />}
                  label={option.label}
                  sx={{ '& .MuiFormControlLabel-label': { fontSize: '14px' } }}
                />
              ))}
            </RadioGroup>
          </Box>
        ))}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || !data?.items?.length}
            sx={{
              background: '#262626', color: '#fff', borderRadius: '8px',
              padding: '8px 24px', fontSize: '14px', fontWeight: 600,
              '&:hover': { background: '#000000' },
              '&.Mui-disabled': { background: '#ccc' },
            }}>
            {saveMutation.isPending ? '저장 중...' : '저장'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default AutoAttendanceSettings;
